import { useState, useEffect, useCallback } from "react";
import { useTheme } from "@/lib/ThemeContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import StyledPanel from "@/components/StyledPanel";
import { Gift, Users, Copy, Check, Star, Trophy, Sparkles, Share2, Shield, Zap } from "lucide-react";

const MILESTONE_ICONS = { 1: Zap, 3: Shield, 5: Sparkles, 10: Trophy };
const MILESTONE_COLORS = { 1: "#22C55E", 3: "#3B82F6", 5: "#8B5CF6", 10: "#F59E0B" };

export default function ReferralPanel({ open, onClose }) {
  const { tokens: t, styleType } = useTheme();
  const isGame = styleType === "game";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const headingCls = isGame ? "font-retro text-[10px]" : "text-xs font-semibold";
  const bodyCls = isGame ? "font-retro-body text-[12px]" : "text-[13px]";
  const labelCls = isGame ? "font-retro text-[8px]" : "text-[10px] font-medium";
  const btnCls = isGame ? "font-retro text-[9px]" : "text-[10px] font-medium";

  const load = useCallback(async () => {
    try {
      const res = await api.getReferralInfo();
      setData(res);
    } catch { toast.error("Failed to load referral info"); }
    setLoading(false);
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleCopy = () => {
    if (!data?.referral_code) return;
    const url = `${window.location.origin}/login?ref=${data.referral_code}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Fallback
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleClaim = async (milestone) => {
    try {
      await api.claimReferralReward(milestone);
      track("reward_earned", { type: "referral" });
      toast.success("Reward claimed! +50 XP");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to claim");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose} data-testid="referral-panel">
      <div className="max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto rounded-2xl"
        style={{ background: t.bgCard, border: `1px solid ${t.borderColor}` }}
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 z-10 px-5 py-4 flex items-center justify-between"
          style={{ background: t.bgCard, borderBottom: `1px solid ${t.borderColor}` }}>
          <div className="flex items-center gap-2">
            <Share2 className="h-4 w-4" style={{ color: t.accent }} />
            <h2 className={headingCls} style={{ color: t.accent }}>
              {isGame ? "REFERRAL PROGRAM" : "Invite Friends"}
            </h2>
          </div>
          <button onClick={onClose} className={`${btnCls} px-2 py-1 rounded`}
            style={{ color: t.textMuted }} data-testid="referral-close">CLOSE</button>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <span className={`${bodyCls} animate-pulse`} style={{ color: t.textMuted }}>Loading...</span>
          </div>
        ) : data ? (
          <div className="p-5 space-y-4">
            {/* Referral Code Card */}
            <div className="p-4 rounded-xl text-center space-y-3"
              style={{ background: `${t.accent}08`, border: `1px solid ${t.accent}20` }}>
              <p className={`${labelCls}`} style={{ color: t.textSecondary }}>
                {isGame ? "YOUR REFERRAL CODE" : "Your Referral Link"}
              </p>
              <div className="flex items-center justify-center gap-2">
                <code className={`${bodyCls} px-3 py-1.5 rounded-lg font-mono`}
                  style={{ background: `${t.accent}15`, color: t.accent, border: `1px solid ${t.accent}30` }}
                  data-testid="referral-code">
                  {data.referral_code}
                </code>
                <button onClick={handleCopy}
                  className="p-2 rounded-lg transition-all hover:scale-105"
                  style={{ background: copied ? "#10B98115" : `${t.accent}15`, color: copied ? "#10B981" : t.accent }}
                  data-testid="referral-copy-btn">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-[10px]" style={{ color: t.textMuted }}>
                Share this link and both you and your friend get rewards!
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              <StyledPanel title={isGame ? "REFERRED" : "Friends Invited"} color={t.accent2}>
                <div className="flex items-center justify-between">
                  <span className={`${isGame ? "font-retro text-lg crt-glow" : "text-lg font-bold"}`}
                    style={{ color: t.accent2 }} data-testid="referral-count">
                    {data.referral_count}
                  </span>
                  <Users className="h-4 w-4" style={{ color: `${t.accent2}40` }} />
                </div>
              </StyledPanel>
              <StyledPanel title={isGame ? "NEXT REWARD" : "Next Reward"} color={t.accent3}>
                <span className={`${labelCls}`} style={{ color: t.accent3 }}>
                  {data.next_reward ? `${data.next_reward.milestone - data.referral_count} more` : "All claimed!"}
                </span>
              </StyledPanel>
            </div>

            {/* Reward Milestones */}
            <div className="space-y-2">
              <h3 className={`${headingCls}`} style={{ color: t.textSecondary }}>
                {isGame ? "MILESTONE REWARDS" : "Reward Milestones"}
              </h3>
              {data.rewards?.map((r) => {
                const Icon = MILESTONE_ICONS[r.milestone] || Gift;
                const color = MILESTONE_COLORS[r.milestone] || t.accent;
                return (
                  <div key={r.milestone} className="flex items-center gap-3 p-3 rounded-xl transition-all"
                    style={{
                      background: r.unlocked ? `${color}08` : "transparent",
                      border: `1px solid ${r.unlocked ? color + "30" : t.borderColor}`,
                      opacity: r.unlocked ? 1 : 0.6,
                    }}
                    data-testid={`referral-milestone-${r.milestone}`}>
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                      style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
                      <Icon className="h-4 w-4" style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`${bodyCls} font-semibold`} style={{ color: r.unlocked ? color : t.textPrimary }}>
                          {r.label}
                        </span>
                        <span className={`${labelCls} px-1.5 py-0.5 rounded`}
                          style={{ color, background: `${color}15` }}>
                          {r.milestone} {r.milestone === 1 ? "friend" : "friends"}
                        </span>
                      </div>
                      <p className="text-[10px]" style={{ color: t.textMuted }}>{r.description}</p>
                    </div>
                    {r.unlocked && !r.claimed && (
                      <button onClick={() => handleClaim(r.milestone)}
                        className={`${btnCls} px-3 py-1.5 rounded-lg border shrink-0 transition-all hover:scale-105`}
                        style={{ borderColor: color, color }}
                        data-testid={`referral-claim-${r.milestone}`}>
                        CLAIM
                      </button>
                    )}
                    {r.claimed && (
                      <span className={`${labelCls} px-2 py-1 rounded-lg shrink-0`}
                        style={{ background: `${color}15`, color }}>
                        CLAIMED
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Recent Referrals */}
            {data.referrals?.length > 0 && (
              <div className="space-y-1.5">
                <h3 className={`${headingCls}`} style={{ color: t.textSecondary }}>
                  {isGame ? "RECENT REFERRALS" : "Recent Referrals"}
                </h3>
                {data.referrals.slice(0, 5).map((r) => (
                  <div key={`${r.email}-${r.date}`} className="flex items-center gap-2 py-1.5 px-2 rounded"
                    style={{ background: `${t.accent2}05` }}>
                    <Users className="h-3 w-3" style={{ color: `${t.accent2}60` }} />
                    <span className={`${bodyCls} flex-1`} style={{ color: t.textSecondary }}>
                      {r.email?.replace(/(.{2})(.*)(@.*)/, "$1***$3")}
                    </span>
                    <span className={`${labelCls}`} style={{ color: t.textMuted }}>
                      {r.date?.slice(0, 10)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
